import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import SearchInput from "../../../common/components/SearchInput/SearchInput";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import logoWixarPlaceholder from "../../../common/util/logoWixarPlaceholder";
import { AppDispatch } from "../../../common/state/store";
import { setAlert } from "../../alert/state/alertsSlice";
import {
  fetchProjectGroupsWithProjects,
  fetchSearchIndex,
  getAllProjectGroupsWithNestedElements,
  getSearchIndex,
  ISearchIndexItem,
} from "../../projectGroups/state/projectGroupsSlice";
import { STATS_FETCH_ALL_LIMIT } from "../../statsNew/constants";
import { copySceneToProject, fetchUnlinkedScenes } from "../state/graphLegacy";

type View = "SELECT_PROJECT" | "REPLACE_SCENE";

interface UnlinkedScene {
  contentSceneId: number;
  contentId: number;
  name: string;
  sceneType: string;
}

interface CopySceneToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceContentSceneId: number;
  sourceProjectId: number;
  currentProjectGroupId: number | null;
  onSuccess?: () => void;
}

const COPY_SCENE_COMPONENT = "copySceneCopy";

const CopySceneToProjectModal: React.FC<CopySceneToProjectModalProps> = ({
  isOpen,
  onClose,
  sourceContentSceneId,
  sourceProjectId,
  currentProjectGroupId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const searchIndex = useSelector(getSearchIndex);
  const allPGs = useSelector(getAllProjectGroupsWithNestedElements);

  const [view, setView] = useState<View>("SELECT_PROJECT");
  const [searchValue, setSearchValue] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [unlinkedScenes, setUnlinkedScenes] = useState<UnlinkedScene[]>([]);
  const [replaceSceneId, setReplaceSceneId] = useState<number | null>(null);

  // Fetch search index on mount if empty
  useEffect(() => {
    if (isOpen && searchIndex.length === 0) {
      dispatch(fetchSearchIndex({} as any));
    }
  }, [isOpen, searchIndex.length, dispatch]);

  // The project list comes from searchIndex; allWithNestedElements only powers
  // the (cosmetic) thumbnail map. The app shell no longer pre-loads it off-courses,
  // so backfill the full list once on open if it's empty (a cold deep-link to the
  // graph). Guarded by length so it never re-fires when reached via /courses.
  useEffect(() => {
    if (isOpen && allPGs.length === 0) {
      dispatch(fetchProjectGroupsWithProjects({ limit: STATS_FETCH_ALL_LIMIT }));
    }
  }, [isOpen, allPGs.length, dispatch]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setView("SELECT_PROJECT");
      setSearchValue("");
      setSelectedProjectId(null);
      setUnlinkedScenes([]);
      setReplaceSceneId(null);
    }
  }, [isOpen]);

  // Build project thumbnail map from allWithNestedElements
  const thumbnailMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const pg of allPGs) {
      for (const el of pg.project_group_elements) {
        if (el.project?.source_thumbnail?.s3_url) {
          map.set(el.project.id, el.project.source_thumbnail.s3_url);
        }
      }
    }
    return map;
  }, [allPGs]);

  // Group projects by project-group with sticky headers. The current module
  // stays listed (with a warning badge): it's the only path to REPLACE one of
  // its own scenes — vital when the module is at the unlinked-scenes cap and
  // the quick menu duplication ("Dupliquer dans ce module") gets a 406.
  const groupedProjects = useMemo(() => {
    const projects = searchIndex.filter((item) => item.type === "project");
    const pgNameMap = new Map<number, string>();
    for (const item of searchIndex) {
      if (item.type === "projectGroup") {
        pgNameMap.set(item.id, item.name);
      }
    }

    const groups = new Map<number, { name: string; projects: ISearchIndexItem[] }>();
    for (const project of projects) {
      const pgId = project.projectGroupId;
      if (!groups.has(pgId)) {
        groups.set(pgId, { name: pgNameMap.get(pgId) || `Group ${pgId}`, projects: [] });
      }
      const group = groups.get(pgId);
      if (group) group.projects.push(project);
    }

    // Sort: current PG first, then alphabetical
    return Array.from(groups.entries()).sort(([aId, aGroup], [bId, bGroup]) => {
      if (aId === currentProjectGroupId) return -1;
      if (bId === currentProjectGroupId) return 1;
      return aGroup.name.localeCompare(bGroup.name);
    });
  }, [searchIndex, currentProjectGroupId, sourceProjectId]);

  // Filter by search
  const filteredGroups = useMemo(() => {
    if (!searchValue.trim()) return groupedProjects;
    const lower = searchValue.toLowerCase();
    return groupedProjects
      .map(
        ([pgId, group]) =>
          [
            pgId,
            {
              ...group,
              projects: group.projects.filter(
                (p) =>
                  p.name.toLowerCase().includes(lower) || group.name.toLowerCase().includes(lower),
              ),
            },
          ] as [number, { name: string; projects: ISearchIndexItem[] }],
      )
      .filter(([, group]) => group.projects.length > 0);
  }, [groupedProjects, searchValue]);

  const handleProjectSelect = useCallback(
    async (projectId: number) => {
      if (projectId === selectedProjectId) return;
      setSelectedProjectId(projectId);
      setUnlinkedScenes([]);

      try {
        const result = await dispatch(
          fetchUnlinkedScenes({ projectId, componentId: "copySceneFetchUnlinked" }),
        ).unwrap();
        const scenes: UnlinkedScene[] = result.data;
        setUnlinkedScenes(scenes);

        if (scenes.length >= 15) {
          setView("REPLACE_SCENE");
        }
      } catch {
        dispatch(
          setAlert({ isOpen: true, msg: t("alerts.fetchUnlinkedScenesFail"), type: "danger" }),
        );
      }
    },
    [dispatch, t, selectedProjectId],
  );

  const handleCopy = useCallback(
    async (replaceContentSceneId?: number) => {
      if (!selectedProjectId) return;

      try {
        await dispatch(
          copySceneToProject({
            sourceContentSceneId,
            sourceProjectId,
            targetProjectId: selectedProjectId,
            replaceContentSceneId,
            componentId: COPY_SCENE_COMPONENT,
          }),
        ).unwrap();

        dispatch(setAlert({ isOpen: true, msg: t("alerts.sceneCopySuccess"), type: "success" }));
        onSuccess?.();
        onClose();
      } catch {
        dispatch(setAlert({ isOpen: true, msg: t("alerts.sceneCopyFail"), type: "danger" }));
      }
    },
    [dispatch, selectedProjectId, sourceContentSceneId, sourceProjectId, t, onSuccess, onClose],
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {view === "SELECT_PROJECT"
              ? t("pages.project.edit.selectTargetModule")
              : t("pages.project.edit.tooManyUnlinkedScenes")}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {view === "SELECT_PROJECT" && (
            <>
              <SearchInput
                value={searchValue}
                onChange={setSearchValue}
                placeholder={t("pages.project.edit.searchModule")}
                className="mb-3"
              />

              <div className="border border-gray-200 rounded-lg h-[50vh] overflow-y-auto">
                {filteredGroups.map(([pgId, group]) => (
                  <div key={pgId}>
                    <div className="px-3 py-2 bg-gray-100 text-sm font-semibold text-gray-700 sticky top-0 border-b border-gray-200">
                      {group.name}
                    </div>
                    {group.projects.map((project) => {
                      const isSelected = selectedProjectId === project.id;
                      const thumbUrl = thumbnailMap.get(project.id);
                      return (
                        <div
                          key={project.id}
                          onClick={() => handleProjectSelect(project.id)}
                          className={`pl-3 pr-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 flex items-center gap-3 transition-colors ${
                            isSelected
                              ? "bg-green-100 border-l-[3px] border-l-green-500 pl-[9px]"
                              : ""
                          }`}
                        >
                          <img
                            src={thumbUrl || logoWixarPlaceholder}
                            alt=""
                            className={`w-8 h-6 rounded object-cover flex-shrink-0 bg-gray-200 ${
                              isSelected ? "ring-2 ring-green-400" : ""
                            }`}
                          />
                          <span
                            className={
                              isSelected
                                ? "text-green-700 font-semibold truncate"
                                : "text-gray-900 truncate"
                            }
                          >
                            {project.name}
                          </span>
                          {isSelected && (
                            <svg
                              className="w-4 h-4 text-green-600 flex-shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                          {project.id === sourceProjectId && (
                            <span className="text-xs text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded ml-auto flex-shrink-0">
                              {t("pages.project.edit.sameModuleWarning")}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}

                {filteredGroups.length === 0 && (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    {searchValue
                      ? t("pages.project.edit.noMatchingModules")
                      : t("pages.project.edit.noModulesAvailable")}
                  </div>
                )}
              </div>
            </>
          )}

          {view === "REPLACE_SCENE" && (
            <div className="border border-gray-200 rounded-lg h-[50vh] overflow-y-auto">
              {unlinkedScenes.map((scene) => (
                <label
                  key={scene.contentSceneId}
                  className={`flex items-center px-3 py-2 text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0 cursor-pointer ${
                    replaceSceneId === scene.contentSceneId ? "bg-red-50" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="replaceScene"
                    checked={replaceSceneId === scene.contentSceneId}
                    onChange={() => setReplaceSceneId(scene.contentSceneId)}
                    className="mr-3 text-green-500 focus:ring-green-500"
                  />
                  <span className="text-gray-800">{scene.name}</span>
                  <span className="ml-2 text-xs text-gray-400">{scene.sceneType}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
          {view === "REPLACE_SCENE" ? (
            <>
              <button
                onClick={() => {
                  setView("SELECT_PROJECT");
                  setReplaceSceneId(null);
                  setSelectedProjectId(null);
                  setUnlinkedScenes([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                &larr; {t("general.back")}
              </button>
              <div className="inline-flex items-center justify-center min-w-[140px] h-[36px]">
                <IsLoading
                  componentId={COPY_SCENE_COMPONENT}
                  showSuccess={false}
                  spinnerStyle={{ width: 20, height: 20, position: "static", margin: 0 }}
                >
                  <button
                    onClick={() => handleCopy(replaceSceneId ?? undefined)}
                    disabled={!replaceSceneId}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      !replaceSceneId
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-green-600 text-white hover:bg-green-700"
                    }`}
                  >
                    {t("pages.project.edit.replaceAndCopy")}
                  </button>
                </IsLoading>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {t("general.cancel")}
              </button>
              <div className="inline-flex items-center justify-center min-w-[80px] h-[36px]">
                <IsLoading
                  componentId={COPY_SCENE_COMPONENT}
                  showSuccess={false}
                  spinnerStyle={{ width: 20, height: 20, position: "static", margin: 0 }}
                >
                  <button
                    onClick={() => handleCopy()}
                    disabled={!selectedProjectId}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      !selectedProjectId
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-green-600 text-white hover:bg-green-700"
                    }`}
                  >
                    {t("pages.project.edit.copy")}
                  </button>
                </IsLoading>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CopySceneToProjectModal;
